import {
  REQUIRED_CNAME,
  WSP_SITE_EXTERNAL_IP,
  MULTISITE_EDITION_NAME,
  WSP_MS_SITE_EXTERNAL_IP,
  MS_CNAME,
} from '../../../globals';
import { ARecord, CAARecord, CNameRecord, DNSRecord, DNSRecordType } from '../../core/dns';
import * as Psl from 'psl';

export class DnsInstruction {
  public static readonly DEFAULT_CURRENT_VALUE: string = 'PAGES.DOMAINS.INCORRECT_DNS.NOT_FOUND';

  private static CORRECT_CAA_VALUES: string[] = [
    'false issue letsencrypt.org',
    'false issuewild letsencrypt.org',
    'true issue letsencrypt.org',
    'true issuewild letsencrypt.org',
  ];

  public host: string;
  public recordType: DNSRecordType;
  public targetValue: string;
  public currentValue: string;

  public get correct(): boolean {
    return this.currentValue === this.targetValue;
  }

  public static buildEmptyDnsRecord(domain: string, editionName: string): DnsInstruction {
    const inst = new DnsInstruction();
    inst.host = domain;
    inst.currentValue = DnsInstruction.DEFAULT_CURRENT_VALUE;
    inst.recordType = DnsInstruction.requiredRecordType(domain);
    inst.targetValue = this.requiredTargetValue(inst.recordType, editionName);
    return inst;
  }

  public static requiredRecordType(domain: string): DNSRecordType {
    if (domain === Psl.parse(domain).domain) {
      return 'A_RECORD';
    }
    return 'CNAME_RECORD';
  }

  public static lookupRecordType(domain: string): DNSRecordType[] {
    if (domain === Psl.parse(domain).domain) {
      return ['A_RECORD', 'AAAA_RECORD', 'CAA_RECORD'];
    }
    return ['CNAME_RECORD', 'CAA_RECORD'];
  }

  public static buildFromDNSRecord(record: DNSRecord, editionName: string): DnsInstruction[] {
    const instructions = [];
    if (record.isEmpty()) {
      instructions.push(DnsInstruction.buildEmptyDnsRecord(record.domain, editionName));
    } else {
      if (record.aRecord.values.length !== 0) {
        const insts = this.buildFromARecord(record.domain, record.aRecord, editionName);
        instructions.push(...insts);
      }
      if (record.cnameRecord.value !== null) {
        instructions.push(this.buildFromCNameRecord(record.domain, record.cnameRecord, editionName));
      }
      if (record.caaRecord.values.length !== 0) {
        const insts = this.buildFromCAARecord(record.domain, record.caaRecord, editionName);
        instructions.push(...insts);
      }
    }
    return instructions;
  }

  public static buildFromARecord(domain: string, record: ARecord, editionName: string): DnsInstruction[] {
    const instructions = [];

    for (const val of record.values) {
      const inst = new DnsInstruction();
      inst.host = domain;
      inst.recordType = 'A_RECORD';
      inst.currentValue = val;
      inst.targetValue = DnsInstruction.requiredTargetValue(inst.recordType, editionName);

      instructions.push(inst);
    }

    return instructions;
  }

  public static buildFromCNameRecord(domain: string, record: CNameRecord, editionName: string): DnsInstruction {
    const inst = new DnsInstruction();
    let val = record.value;
    if (val.endsWith('.')) {
      val = val.substr(0, val.length - 1);
    }
    inst.host = domain;
    inst.recordType = 'CNAME_RECORD';
    inst.currentValue = val;
    inst.targetValue = DnsInstruction.requiredTargetValue(inst.recordType, editionName);

    return inst;
  }

  public static buildFromCAARecord(domain: string, record: CAARecord, editionName: string): DnsInstruction[] {
    const instructions = [];

    for (const val of record.values) {
      const caaValue = String(val.issuerCriticalFlag) + ' ' + val.tag + ' ' + val.value;
      if (DnsInstruction.isCAAValueCorrect(caaValue)) {
        continue;
      }

      const inst = new DnsInstruction();
      inst.host = domain;
      inst.recordType = 'CAA_RECORD';
      inst.currentValue =
        String(DnsInstruction.issuerCriticalToInt(val.issuerCriticalFlag)) + ' ' + val.tag + ' ' + val.value;
      inst.targetValue = DnsInstruction.requiredTargetValue(inst.recordType, editionName);

      instructions.push(inst);
    }

    return instructions;
  }

  public static issuerCriticalToInt(issuerCriticalFlag: boolean): number {
    if (issuerCriticalFlag) {
      return 128;
    }
    return 0;
  }

  public static isCAAValueCorrect(value: string): boolean {
    for (const val of DnsInstruction.CORRECT_CAA_VALUES) {
      if (value === val) {
        return true;
      }
    }
    return false;
  }

  public static requiredTargetValue(recordType: DNSRecordType, editionName: string): string {
    let result: string;

    switch (recordType) {
      case 'A_RECORD':
        if (editionName == MULTISITE_EDITION_NAME) {
          result = WSP_MS_SITE_EXTERNAL_IP;
        } else {
          result = WSP_SITE_EXTERNAL_IP;
        }
        break;
      case 'CNAME_RECORD':
        if (editionName == MULTISITE_EDITION_NAME) {
          result = MS_CNAME;
        } else {
          result = REQUIRED_CNAME;
        }
        break;
      case 'CAA_RECORD':
        result = '128 issue letsencrypt.org';
        break;
      default:
        if (editionName == MULTISITE_EDITION_NAME) {
          result = MS_CNAME;
        } else {
          result = REQUIRED_CNAME;
        }
        break;
    }
    return result;
  }

  public clearCurrentValue(): void {
    this.currentValue = DnsInstruction.DEFAULT_CURRENT_VALUE;
  }
}
